import { useState, useEffect, useRef, useCallback, lazy, Suspense, type PointerEvent as ReactPointerEvent, type TouchEvent as ReactTouchEvent } from 'react';
import type { Idea, Tag, User, IdeaRelation } from '../api';
import * as api from '../api';
import { StatusBadge, SourceIcon, TagChip } from './StatusBadge';
import { RecurrenceBadge } from './RecurrenceBadge';
import { OpenIssueSection } from './OpenIssueSection';
import { SessionsSection } from './SessionsSection';
import { BuildSection } from './BuildSection';
import { BuildHistorySection } from './BuildHistorySection';
import {
  CLAUDE_ACTIONS,
  buildClaudePrompt,
  claudeUrl,
  loadClaudeTarget,
  saveClaudeTarget,
  type ClaudeTarget,
} from '../utils/open-in-claude';
import { reloadOnChunkError } from '../utils/lazy-view';

// react-markdown + highlight.js live in their own chunk (P3-14). The fetch starts when this
// module loads, off the first-paint path, so it is normally ready before a note is opened.
const markdownChunk = import('./MarkdownBody');
const MarkdownBody = lazy(reloadOnChunkError(() => markdownChunk.then((m) => ({ default: m.MarkdownBody }))));

const MIN_PANEL_WIDTH = 160;
const MAX_PANEL_WIDTH = 360;
const STORAGE_KEY = 'nrepo:detail-panel-width';

function loadPanelWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const n = Number(stored);
      if (n >= MIN_PANEL_WIDTH && n <= MAX_PANEL_WIDTH) return n;
    }
  } catch { /* ignore */ }
  return MAX_PANEL_WIDTH;
}

const STATUSES = ['captured', 'exploring', 'building', 'shipped', 'shelved'] as const;

export function DetailPanel({
  idea,
  tags,
  user,
  onClose,
  onUpdate,
  onNavigateSettings,
  expanded,
  onToggleExpand,
}: {
  idea: Idea;
  tags: Tag[];
  user: User;
  onClose: () => void;
  onUpdate: () => void;
  onNavigateSettings?: () => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [bodyTab, setBodyTab] = useState<'preview' | 'edit'>('preview');
  const [title, setTitle] = useState(idea.title);
  const [body, setBody] = useState(idea.body ?? '');
  const [saving, setSaving] = useState(false);
  const [developing, setDeveloping] = useState(false);
  const [developError, setDevelopError] = useState<string | null>(null);
  const [claudeTarget, setClaudeTarget] = useState<ClaudeTarget>(loadClaudeTarget);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(panelWidth);

  // Mobile swipe-to-close
  const panelRef = useRef<HTMLDivElement>(null);
  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  const swipeStartTime = useRef(0);
  const swiping = useRef(false);
  const currentDx = useRef(0);

  const onTouchStart = useCallback((e: ReactTouchEvent<HTMLDivElement>) => {
    if (window.innerWidth >= 768) return;
    const touch = e.touches[0];
    if (!touch) return;
    swipeStartX.current = touch.clientX;
    swipeStartY.current = touch.clientY;
    swipeStartTime.current = Date.now();
    swiping.current = false;
    currentDx.current = 0;
  }, []);

  const onTouchMove = useCallback((e: ReactTouchEvent<HTMLDivElement>) => {
    if (window.innerWidth >= 768) return;
    if (swipeStartX.current === 0) return;
    const touch = e.touches[0];
    if (!touch) return;
    const dx = touch.clientX - swipeStartX.current;
    const dy = Math.abs(touch.clientY - swipeStartY.current);
    // Only swipe right, and horizontal must dominate vertical
    if (dx < 0) return;
    if (dx > 10 && dx > dy * 1.5) {
      swiping.current = true;
      currentDx.current = dx;
      if (panelRef.current) {
        panelRef.current.style.transition = 'none';
        panelRef.current.style.transform = `translateX(${dx}px)`;
        panelRef.current.style.opacity = String(Math.max(0, 1 - dx / window.innerWidth));
      }
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (window.innerWidth >= 768 || !swiping.current) {
      swipeStartX.current = 0;
      return;
    }
    const el = panelRef.current;
    if (!el) { swipeStartX.current = 0; return; }

    const dx = currentDx.current;
    const elapsed = Date.now() - swipeStartTime.current;
    const velocity = dx / Math.max(elapsed, 1);

    if (dx > 100 || velocity > 0.5) {
      // Animate off screen then close
      el.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
      el.style.transform = `translateX(${window.innerWidth}px)`;
      el.style.opacity = '0';
      setTimeout(() => {
        el.style.transform = '';
        el.style.opacity = '';
        el.style.transition = '';
        onClose();
      }, 200);
    } else {
      // Snap back
      el.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
      el.style.transform = '';
      el.style.opacity = '';
      setTimeout(() => { el.style.transition = ''; }, 200);
    }
    swipeStartX.current = 0;
    swiping.current = false;
    currentDx.current = 0;
  }, [onClose]);

  const onResizePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = panelWidth;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  const onResizePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    // Dragging left increases width (panel is on the right)
    const delta = startXRef.current - e.clientX;
    const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidthRef.current + delta));
    setPanelWidth(newWidth);
  }, []);

  const onResizePointerUp = useCallback(() => {
    draggingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try { localStorage.setItem(STORAGE_KEY, String(panelWidth)); } catch { /* ignore */ }
  }, [panelWidth]);

  // Reset edit state when idea changes
  useEffect(() => {
    setEditing(false);
    setBodyTab('preview');
    setTitle(idea.title);
    setBody(idea.body ?? '');
  }, [idea.id, idea.title, idea.body]);

  // Listen for Cmd+Shift+P toggle from App
  useEffect(() => {
    if (!expanded) return;
    const handler = () => {
      setBodyTab((prev) => {
        if (prev === 'preview') {
          setEditing(true);
          return 'edit';
        }
        return 'preview';
      });
    };
    window.addEventListener('nrepo:toggle-body-tab', handler);
    return () => window.removeEventListener('nrepo:toggle-body-tab', handler);
  }, [expanded]);

  const isRecent = Date.now() - new Date(idea.created_at + 'Z').getTime() < 120_000;

  // Poll for vectorize_id if not yet populated (only for recent ideas)
  useEffect(() => {
    if (idea.vectorize_id || !isRecent) return;

    let cancelled = false;
    pollRef.current = setInterval(async () => {
      if (cancelled) return;
      try {
        const updated = await api.getIdea(idea.id);
        if (cancelled) return;
        if (updated.vectorize_id) {
          if (pollRef.current) clearInterval(pollRef.current);
          onUpdate();
        }
      } catch {
        // ignore
      }
    }, 2000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [idea.id, idea.vectorize_id, isRecent, onUpdate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateIdea(idea.id, { title, body: body || undefined });
      setEditing(false);
      onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    await api.updateIdea(idea.id, { status });
    onUpdate();
  };

  const hasByokKey = user.has_anthropic_key || user.has_openai_key || user.has_openrouter_key;

  const handleDevelop = async () => {
    if (!hasByokKey) return;
    setDeveloping(true);
    setDevelopError(null);
    try {
      await api.developIdea(idea.id);
      onUpdate();
    } catch (err) {
      const message = (err as Error).message;
      setDevelopError(message);
    } finally {
      setDeveloping(false);
    }
  };

  const handleArchive = async () => {
    await api.archiveIdea(idea.id);
    onUpdate();
    onClose();
  };

  const handleTagToggle = async (tagName: string) => {
    const currentTags = idea.tags ?? [];
    const newTags = currentTags.includes(tagName)
      ? currentTags.filter((t) => t !== tagName)
      : [...currentTags, tagName];
    await api.updateIdea(idea.id, { tags: newTags });
    onUpdate();
  };

  // Click gutter (empty space beside centered content) to close expanded view
  const handleGutterClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!expanded || !onToggleExpand) return;
    const target = e.target as HTMLElement;
    // Only close if clicking the panel background or the body wrapper — not interactive content
    if (target.classList.contains('detail-panel-expanded') || target.classList.contains('detail-body')) {
      onToggleExpand();
    }
  }, [expanded, onToggleExpand]);

  // Link to... popover state
  const [linkSearchOpen, setLinkSearchOpen] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState<Idea[]>([]);
  const [linkSearching, setLinkSearching] = useState(false);
  const [linkType, setLinkType] = useState('related');

  const handleLinkSearch = useCallback(async (q: string) => {
    setLinkSearchQuery(q);
    if (q.length < 2) { setLinkSearchResults([]); return; }
    setLinkSearching(true);
    try {
      const res = await api.searchIdeas(q, 'keyword');
      setLinkSearchResults(res.results.filter((r: Idea) => r.id !== idea.id).slice(0, 5));
    } catch { setLinkSearchResults([]); }
    finally { setLinkSearching(false); }
  }, [idea.id]);

  const handleCreateLink = async (targetId: number) => {
    try {
      await api.createRelation(idea.id, targetId, linkType);
      setLinkSearchOpen(false);
      setLinkSearchQuery('');
      setLinkSearchResults([]);
      onUpdate();
    } catch (err) {
      // Could show error toast
    }
  };

  // Group relations by type and direction
  const RELATION_DISPLAY: Record<string, { outLabel: string; inLabel: string; arrow: string }> = {
    blocks: { outLabel: 'Blocks', inLabel: 'Blocked by', arrow: '→' },
    inspires: { outLabel: 'Inspires', inLabel: 'Inspired by', arrow: '→' },
    supersedes: { outLabel: 'Supersedes', inLabel: 'Superseded by', arrow: '→' },
    // A parent row's source is the sub-idea and its target the parent (parent_id, /mcp/v2 link_ideas).
    parent: { outLabel: 'Sub-idea of', inLabel: 'Sub-ideas', arrow: '→' },
    related: { outLabel: 'Related', inLabel: 'Related', arrow: '↔' },
    duplicate: { outLabel: 'Similar', inLabel: 'Similar', arrow: '↔' },
  };

  const allRelations = idea.relations ?? [];
  const limit = expanded ? 20 : 6;

  // Build grouped display: outgoing and incoming
  const relationGroups: { label: string; arrow: string; items: IdeaRelation[] }[] = [];

  // Outgoing: this idea is the source
  const outgoing = allRelations.filter((r) => r.source_idea_id === idea.id);
  // Incoming: this idea is the target
  const incoming = allRelations.filter((r) => r.target_idea_id === idea.id);

  // Group outgoing by type
  const outByType = new Map<string, IdeaRelation[]>();
  for (const r of outgoing) {
    const list = outByType.get(r.relation_type) ?? [];
    list.push(r);
    outByType.set(r.relation_type, list);
  }
  for (const [type, items] of outByType) {
    const display = RELATION_DISPLAY[type] ?? { outLabel: type, inLabel: type, arrow: '→' };
    relationGroups.push({ label: `${display.outLabel} ${display.arrow}`, arrow: display.arrow, items });
  }

  // Group incoming by type
  const inByType = new Map<string, IdeaRelation[]>();
  for (const r of incoming) {
    const list = inByType.get(r.relation_type) ?? [];
    list.push(r);
    inByType.set(r.relation_type, list);
  }
  for (const [type, items] of inByType) {
    const display = RELATION_DISPLAY[type] ?? { outLabel: type, inLabel: type, arrow: '←' };
    // For bidirectional types, skip incoming if already shown as outgoing
    if ((type === 'related' || type === 'duplicate') && outByType.has(type)) continue;
    relationGroups.push({ label: `${display.inLabel} ←`, arrow: '←', items });
  }

  const totalRelations = allRelations.length;

  return (
    <div
      ref={panelRef}
      className={`detail-panel${expanded ? ' detail-panel-expanded' : ''}`}
      onClick={handleGutterClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={!expanded ? { width: panelWidth, minWidth: panelWidth } : undefined}
    >
      <div className="mobile-swipe-hint" />
      {!expanded && (
        <div
          className="detail-resize-handle"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
        />
      )}
      <div className="detail-header">
        {expanded && onToggleExpand && (
          <button className="detail-back-btn" onClick={onToggleExpand} title="Back to list">
            ← Back
          </button>
        )}
        <span className="idea-id mono">#{idea.number}</span>
        {!idea.vectorize_id && isRecent && (
          <span className="analyzing-badge">
            <span className="spinner spinner-sm" /> analyzing...
          </span>
        )}
        {!expanded && onToggleExpand && (
          <button className="btn-icon detail-expand-btn" onClick={onToggleExpand} title="Expand">
            ⇱
          </button>
        )}
        <button className="btn-icon" onClick={onClose} title="Close">
          ✕
        </button>
      </div>

      <div className="detail-body">
        {/* Status */}
        <div className="detail-section">
          <div className="detail-label">STATUS</div>
          <div className="status-selector">
            {STATUSES.map((s) => (
              <button
                key={s}
                className={`status-option${idea.status === s ? ' status-option-active' : ''}`}
                onClick={() => handleStatusChange(s)}
              >
                <StatusBadge status={s} />
              </button>
            ))}
          </div>
          {idea.recurrence && <RecurrenceBadge count={idea.recurrence.count} since={idea.recurrence.since} />}
        </div>

        {/* Title */}
        <div className="detail-section">
          {editing ? (
            <input
              className="detail-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          ) : (
            <h2 className="detail-title" onClick={() => setEditing(true)}>
              {idea.title}
            </h2>
          )}
        </div>

        {/* Body */}
        <div className="detail-section">
          {expanded ? (
            <>
              <div className="detail-body-tabs">
                <div className="detail-label">NOTES</div>
                <div className="body-tab-group">
                  <button
                    className={`body-tab${bodyTab === 'preview' ? ' body-tab-active' : ''}`}
                    onClick={() => setBodyTab('preview')}
                  >
                    Preview
                  </button>
                  <button
                    className={`body-tab${bodyTab === 'edit' ? ' body-tab-active' : ''}`}
                    onClick={() => { setBodyTab('edit'); setEditing(true); }}
                  >
                    Edit
                  </button>
                </div>
              </div>
              {bodyTab === 'edit' ? (
                <textarea
                  className="detail-body-input detail-body-input-expanded"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Add notes... (Markdown supported)"
                />
              ) : (
                idea.body ? (
                  <div className="markdown-body">
                    <Suspense fallback={<p className="markdown-body-plain">{idea.body}</p>}>
                      <MarkdownBody content={idea.body} />
                    </Suspense>
                  </div>
                ) : (
                  <div
                    className="detail-body-text"
                    onClick={() => { setBodyTab('edit'); setEditing(true); }}
                  >
                    <span className="text-muted">Click to add notes...</span>
                  </div>
                )
              )}
            </>
          ) : (
            <>
              <div className="detail-label">NOTES</div>
              {editing ? (
                <textarea
                  className="detail-body-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  placeholder="Add notes..."
                />
              ) : (
                <div
                  className={`detail-body-text${idea.body ? ' detail-body-md' : ''}`}
                  onClick={(e) => {
                    // Links and text selection in the rendered notes shouldn't open the editor.
                    if ((e.target as HTMLElement).closest('a')) return;
                    if (window.getSelection()?.toString()) return;
                    setEditing(true);
                  }}
                >
                  {idea.body ? (
                    <div className="markdown-body">
                      <Suspense fallback={<p className="markdown-body-plain">{idea.body}</p>}>
                        <MarkdownBody content={idea.body} />
                      </Suspense>
                    </div>
                  ) : (
                    <span className="text-muted">Click to add notes...</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {editing && (
          <div className="detail-actions-inline">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="btn btn-secondary" onClick={() => { setEditing(false); setBodyTab('preview'); setTitle(idea.title); setBody(idea.body ?? ''); }}>
              Cancel
            </button>
          </div>
        )}

        {/* Tags */}
        <div className="detail-section">
          <div className="detail-label">TAGS</div>
          <div className="detail-tags">
            {tags.map((tag) => (
              <TagChip
                key={tag.id}
                name={tag.name}
                color={tag.color}
                active={(idea.tags ?? []).includes(tag.name)}
                onClick={() => handleTagToggle(tag.name)}
              />
            ))}
          </div>
        </div>

        {/* Relations */}
        <div className="detail-section">
          <div className="detail-label">
            RELATIONS
            {user.plan === 'pro' ? (
              <button
                className="btn-icon btn-icon-sm"
                onClick={() => setLinkSearchOpen(!linkSearchOpen)}
                title="Link to another idea"
                style={{ marginLeft: 8, fontSize: 12 }}
              >
                +
              </button>
            ) : (
              <span className="pro-badge-inline" title="Upgrade to Pro to create idea links">PRO</span>
            )}
          </div>

          {linkSearchOpen && (
            <div className="link-search-popover">
              <input
                className="link-search-input"
                placeholder="Search ideas to link..."
                value={linkSearchQuery}
                onChange={(e) => handleLinkSearch(e.target.value)}
                autoFocus
              />
              <select
                className="link-type-select"
                value={linkType}
                onChange={(e) => setLinkType(e.target.value)}
              >
                <option value="related">Related</option>
                <option value="blocks">Blocks</option>
                <option value="inspires">Inspires</option>
                <option value="supersedes">Supersedes</option>
                <option value="parent">Sub-idea of</option>
              </select>
              {linkSearching && <div className="text-muted" style={{ padding: '4px 0' }}>Searching...</div>}
              {linkSearchResults.map((result) => (
                <div
                  key={result.id}
                  className="link-search-result"
                  onClick={() => handleCreateLink(result.id)}
                >
                  <span className="mono">#{result.number}</span>
                  <span>{result.title}</span>
                </div>
              ))}
            </div>
          )}

          {totalRelations > 0 ? (
            <div className="detail-relations">
              {relationGroups.slice(0, limit).map((group) => (
                <div key={group.label} className="relation-group">
                  <div className="relation-group-label">{group.label}</div>
                  {group.items.map((rel) => {
                    const relatedId = rel.source_idea_id === idea.id ? rel.target_idea_id : rel.source_idea_id;
                    const title = rel.related_idea_title;
                    const scorePct = rel.score != null ? (rel.score * 100).toFixed(0) : null;
                    let scoreClass = 'score-blue';
                    if (rel.score != null && rel.score > 0.9) scoreClass = 'score-green';
                    else if (rel.score != null && rel.score > 0.7) scoreClass = 'score-amber';

                    return (
                      <div key={rel.id} className="relation-item">
                        <span className="mono">#{rel.related_idea_number ?? relatedId}</span>
                        {title && <span className="relation-title">{title}</span>}
                        {scorePct != null && (
                          <span className={`score-badge ${scoreClass}`}>{scorePct}%</span>
                        )}
                        {rel.note && <div className="relation-note">{rel.note}</div>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted" style={{ fontSize: 12 }}>No relations yet</div>
          )}
        </div>

        {/* Meta */}
        <div className="detail-section">
          <div className="detail-label">META</div>
          <div className="detail-meta">
            <div className="meta-row">
              <span className="text-muted">Source</span>
              <SourceIcon source={idea.source} />
            </div>
            {idea.source_url && (
              <div className="meta-row">
                <span className="text-muted">Source URL</span>
                <a href={idea.source_url} target="_blank" rel="noopener noreferrer" className="meta-link">
                  {new URL(idea.source_url).hostname}
                </a>
              </div>
            )}
            <div className="meta-row">
              <span className="text-muted">Created</span>
              <span>{new Date(idea.created_at + 'Z').toLocaleDateString()}</span>
            </div>
            <div className="meta-row">
              <span className="text-muted">Updated</span>
              <span>{new Date(idea.updated_at + 'Z').toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Links */}
        {idea.links && idea.links.length > 0 && (
          <div className="detail-section">
            <div className="detail-label">LINKED RESOURCES</div>
            <div className="detail-links">
              {idea.links.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-item"
                >
                  <span className="link-type mono">{link.link_type}</span>
                  <span>{link.title ?? link.url}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <SessionsSection idea={idea} />

        <OpenIssueSection idea={idea} user={user} />

        <BuildSection idea={idea} onChanged={onUpdate} />

        <BuildHistorySection idea={idea} isPro={user.plan === 'pro'} />

        {/* Ask Claude: prefilled hand-off to the user's own Claude (P2-06) */}
        <div className="detail-section">
          <div className="detail-body-tabs">
            <div className="detail-label">ASK CLAUDE</div>
            <div className="body-tab-group">
              {(['web', 'desktop'] as const).map((t) => (
                <button
                  key={t}
                  className={`body-tab${claudeTarget === t ? ' body-tab-active' : ''}`}
                  onClick={() => { setClaudeTarget(t); saveClaudeTarget(t); }}
                  title={t === 'web' ? 'Open in claude.ai' : 'Open in the Claude desktop app'}
                >
                  {t === 'web' ? 'Web' : 'Desktop'}
                </button>
              ))}
            </div>
          </div>
          <div className="claude-actions">
            {CLAUDE_ACTIONS.map(({ action, label, hint }) => (
              <a
                key={action}
                className="btn btn-secondary btn-sm"
                href={claudeUrl(buildClaudePrompt(action, idea), claudeTarget)}
                target={claudeTarget === 'web' ? '_blank' : undefined}
                rel="noopener noreferrer"
                title={hint}
              >
                {label}
              </a>
            ))}
          </div>
          <p className="claude-actions-note">Opens Claude with a prompt ready to send. Uses your NeuralRepo connector.</p>
        </div>

        {/* Develop with AI */}
        <div className="detail-section">
          {hasByokKey ? (
            <>
              <button
                className="btn btn-secondary develop-btn"
                onClick={handleDevelop}
                disabled={developing}
              >
                {developing ? (
                  <><span className="spinner spinner-sm" /> Developing...</>
                ) : (
                  'Develop with AI...'
                )}
              </button>
              {developError && (
                <div className="develop-error">{developError}</div>
              )}
            </>
          ) : (
            <div className="develop-cta">
              <p className="settings-desc">Add an AI provider key to develop this idea with AI.</p>
              {onNavigateSettings && (
                <button className="btn btn-ghost-sm" onClick={onNavigateSettings}>
                  Go to Settings
                </button>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="detail-section detail-actions">
          <button className="btn btn-danger" onClick={handleArchive}>
            Archive
          </button>
        </div>
      </div>
    </div>
  );
}
